import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePermissions, Role, Module } from '@/contexts/PermissionContext';
import { 
  MODULE_NAMES, 
  PERMISSION_ACTIONS, 
  MODULE_HIERARCHY, 
  MAIN_MODULES_ORDER,
  ACTION_LABELS,
  getSubmodules,
  isMainModule 
} from '@/constants/modules';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, 
  UserPlus, 
  Users, 
  Settings, 
  Eye, 
  Edit, 
  Plus, 
  Search,
  Filter,
  MoreHorizontal,
  UserCheck,
  UserX
} from 'lucide-react';


// Interface para usuários
interface User {
  id: string;
  email: string;
  created_at: string;
  user_roles: {
    role: Role;
  }[];
}

// Interface para permissões
interface Permission {
  id: string;
  module_id: string;
  action: string;
  description: string;
}

// Interface para associação de permissões a papéis
interface RolePermission {
  role_id: string;
  permission_id: string;
}

const AccessControl = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { userRoles, refreshPermissions } = usePermissions();
  
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [userRoleId, setUserRoleId] = useState<string | null>(null);
  
  // Verificar se o usuário atual é admin
  const isAdmin = userRoles.some(role => 
    role.name.toLowerCase() === 'admin'
  );
  
  // Buscar dados iniciais
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Buscar usuários básicos, sem papéis
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, created_at');
        
        if (usersError) throw usersError;
        
        // Buscar papéis dos usuários em uma consulta separada (apenas ativos)
        const { data: userRolesData, error: userRolesError } = await supabase
          .from('user_roles')
          .select('user_id, role_id')
          .eq('is_active', true);
        
        if (userRolesError) throw userRolesError;
        
        // Buscar papéis
        const { data: rolesData, error: rolesError } = await supabase
          .from('roles')
          .select('*')
          .order('name');
        
        if (rolesError) throw rolesError;
        
        // Transformar os dados para o formato esperado
        const formattedUsers: User[] = (usersData || []).map(userData => {
          // Obter os papéis deste usuário
          const userRoleIds = userRolesData
            ?.filter(ur => ur.user_id === userData.id)
            .map(ur => ur.role_id) || [];
          
          const userRoles = userRoleIds.map(roleId => {
            const role = rolesData?.find(r => r.id === roleId);
            return { role: role as Role };
          }).filter(ur => ur.role); // Filtrar papéis não encontrados
          
          return {
            id: userData.id,
            email: userData.email,
            created_at: userData.created_at,
            user_roles: userRoles
          };
        });
        
        // Buscar módulos
        const { data: modulesData, error: modulesError } = await supabase
          .rpc('get_all_modules');
        
        if (modulesError) throw modulesError;
        
        // Buscar permissões
        const { data: permissionsData, error: permissionsError } = await supabase
          .from('permissions')
          .select('*');
        
        if (permissionsError) throw permissionsError;
        
        // Buscar associações de permissões a papéis
        const { data: rolePermissionsData, error: rolePermissionsError } = await supabase
          .from('role_permissions')
          .select('*');
        
        if (rolePermissionsError) throw rolePermissionsError;
        
        // Atualizar estados
        setUsers(formattedUsers);
        setRoles(rolesData || []);
        setModules(modulesData || []);
        setPermissions(permissionsData || []);
        setRolePermissions(rolePermissionsData || []);
        
        // Selecionar o primeiro papel por padrão
        if (rolesData && rolesData.length > 0) {
          setSelectedRole(rolesData[0].id);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        toast.error('Erro ao carregar dados de controle de acesso');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Verificar se uma permissão está associada ao papel selecionado
  const hasPermission = (permissionId: string): boolean => {
    if (!selectedRole) return false;
    
    return rolePermissions.some(
      rp => rp.role_id === selectedRole && rp.permission_id === permissionId
    );
  };
  
  // Alternar permissão para o papel selecionado (SEM duplicatas)
  const togglePermission = async (permissionId: string) => {
    if (!selectedRole) return;
    
    try {
      const hasPermissionAlready = hasPermission(permissionId);
      
      if (hasPermissionAlready) {
        // Remover permissão
        const { error } = await supabase
          .from('role_permissions')
          .delete()
          .eq('role_id', selectedRole)
          .eq('permission_id', permissionId);
        
        if (error) throw error;
        
        // Atualizar estado local
        setRolePermissions(rolePermissions.filter(
          rp => !(rp.role_id === selectedRole && rp.permission_id === permissionId)
        ));
        
        toast.success('Permissão removida com sucesso');
      } else {
        // Verificar se já existe antes de adicionar
        const { data: existing } = await supabase
          .from('role_permissions')
          .select('id')
          .eq('role_id', selectedRole)
          .eq('permission_id', permissionId)
          .single();
        
        if (existing) {
          toast.info('Permissão já existe');
          return;
        }
        
        // Adicionar permissão usando a nova função RPC
        const { error } = await supabase
          .rpc('insert_role_permission', {
            p_role_id: selectedRole,
            p_permission_id: permissionId
          });
        
        if (error) {
          // Se erro for de duplicata, apenas avisar
          if (error.code === '23505') {
            toast.info('Permissão já existe');
            return;
          }
          throw error;
        }
        
        // Atualizar estado local apenas se não existir
        const existsInState = rolePermissions.some(
          rp => rp.role_id === selectedRole && rp.permission_id === permissionId
        );
        
        if (!existsInState) {
        setRolePermissions([
          ...rolePermissions,
          { role_id: selectedRole, permission_id: permissionId }
        ]);
        }
        
        toast.success('Permissão adicionada com sucesso');
      }
      
      // Atualizar permissões no contexto
      await refreshPermissions();
    } catch (error) {
      console.error('Erro ao alternar permissão:', error);
      toast.error('Erro ao alternar permissão');
    }
  };
  
  // Alternar todas as permissões de uma ação específica (SEM duplicatas)
  const toggleAllPermissionsForAction = async (action: string) => {
    if (!selectedRole) return;
    
    const actionPermissions = permissions.filter(p => p.action === action);
    const checkedPermissions = actionPermissions.filter(p => hasPermission(p.id));
    const shouldEnableAll = checkedPermissions.length < actionPermissions.length;
    
    try {
      if (shouldEnableAll) {
        // Ativar todas as permissões desta ação (apenas as que não estão ativas)
        const permissionsToAdd = actionPermissions.filter(p => !hasPermission(p.id));
        
        // Usar Promise.all para operações paralelas e evitar duplicatas
        const results = await Promise.allSettled(
          permissionsToAdd.map(async (permission) => {
            // Verificar novamente se já existe antes de inserir
            const { data: existing } = await supabase
              .from('role_permissions')
              .select('id')
              .eq('role_id', selectedRole)
              .eq('permission_id', permission.id)
              .single();
            
            if (existing) {
              return { permission, skipped: true };
            }
            
            const { error } = await supabase
              .rpc('insert_role_permission', {
                p_role_id: selectedRole,
                p_permission_id: permission.id
              });
            
            if (error) throw error;
            return { permission, skipped: false };
          })
        );
        
        // Atualizar estado local apenas com as permissões realmente adicionadas
        const addedPermissions = results
          .filter(result => result.status === 'fulfilled' && !result.value.skipped)
          .map(result => (result as PromiseFulfilledResult<any>).value.permission);
        
        setRolePermissions(prev => [
          ...prev,
          ...addedPermissions.map(p => ({ role_id: selectedRole, permission_id: p.id }))
        ]);
        
        toast.success(`${addedPermissions.length} permissões de "${action}" foram ativadas`);
      } else {
        // Desativar todas as permissões desta ação
        const results = await Promise.allSettled(
          checkedPermissions.map(async (permission) => {
            const { error } = await supabase
              .from('role_permissions')
              .delete()
              .eq('role_id', selectedRole)
              .eq('permission_id', permission.id);
            
            if (error) throw error;
            return permission;
          })
        );
        
        const removedPermissions = results
          .filter(result => result.status === 'fulfilled')
          .map(result => (result as PromiseFulfilledResult<any>).value);
        
        setRolePermissions(prev => prev.filter(
          rp => !removedPermissions.some(p => rp.role_id === selectedRole && rp.permission_id === p.id)
        ));
        
        toast.success(`${removedPermissions.length} permissões de "${action}" foram desativadas`);
      }
      
      await refreshPermissions();
    } catch (error) {
      console.error('Erro ao alternar permissões em lote:', error);
      toast.error('Erro ao alternar permissões em lote');
    }
  };
  
  // Criar novo papel
  const createRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Nome do papel é obrigatório');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('roles')
        .insert({
          name: newRoleName.trim(),
          description: newRoleDescription.trim() || null,
          is_system: false
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Atualizar estado local
      setRoles([...roles, data]);
      setSelectedRole(data.id);
      
      // Limpar formulário
      setNewRoleName('');
      setNewRoleDescription('');
      setIsRoleDialogOpen(false);
      
      toast.success('Papel criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar papel:', error);
      toast.error('Erro ao criar papel');
    }
  };
  
  // Atribuir papel a usuário
  const assignRoleToUser = async () => {
    if (!selectedUser || !userRoleId) {
      toast.error('Selecione um usuário e um papel');
      return;
    }
    
    try {
      // Verificar se o usuário já tem esse papel ativo
      const userHasRole = selectedUser.user_roles.some(
        ur => ur.role.id === userRoleId
      );
      
      if (userHasRole) {
        toast.error('Usuário já possui esse papel');
        return;
      }
      
      // Usar a função assign_user_role que lida com inserção ou reativação
      const { data: success, error } = await supabase
        .rpc('assign_user_role', {
          p_user_id: selectedUser.id,
          p_role_id: userRoleId
        });
      
      if (error) throw error;
      
      if (!success) {
        toast.error('Usuário já possui esse papel');
        return;
      }
      
      // Atualizar estado local
      const role = roles.find(r => r.id === userRoleId);
      if (role) {
        const updatedUsers = users.map(u => {
          if (u.id === selectedUser.id) {
            return {
              ...u,
              user_roles: [...u.user_roles, { role }]
            };
          }
          return u;
        });
        
        setUsers(updatedUsers);
        setSelectedUser(null);
        setUserRoleId(null);
        setIsUserDialogOpen(false);
      }
      
      // Atualizar permissões no contexto
      await refreshPermissions();
      
      toast.success('Papel atribuído com sucesso');
    } catch (error) {
      console.error('Erro ao atribuir papel:', error);
      toast.error('Erro ao atribuir papel ao usuário');
    }
  };
  
  // Remover papel de usuário
  const removeRoleFromUser = async (userId: string, roleId: string) => {
    try {
      // Use soft delete function instead of hard delete
      const { error } = await supabase.rpc('deactivate_user_role', {
        p_user_id: userId,
        p_role_id: roleId
      });
      
      if (error) throw error;
      
      // Atualizar estado local imediatamente
      const updatedUsers = users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            user_roles: user.user_roles.filter(ur => ur.role.id !== roleId)
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      
      // Atualizar permissões no contexto
      await refreshPermissions();
      
      toast.success('Papel removido com sucesso');
    } catch (error) {
      console.error('Erro ao remover papel:', error);
      toast.error('Erro ao remover papel do usuário');
    }
  };
  
  // Obter módulos filhos
  const getChildModules = (parentId: string | null): Module[] => {
    return modules.filter(m => m.parent_id === parentId);
  };
  
  // Obter permissões de um módulo
  const getModulePermissions = (moduleId: string): Permission[] => {
    return permissions.filter(p => p.module_id === moduleId);
  };
  
  // Renderizar matriz de permissões organizada por estrutura do sidebar
  const renderPermissionsMatrix = () => {
    const isSystemRole = roles.find(r => r.id === selectedRole)?.is_system;
    
    // Ações que queremos mostrar na matriz
    const actions = [
      { key: PERMISSION_ACTIONS.VIEW, label: ACTION_LABELS[PERMISSION_ACTIONS.VIEW], icon: <Eye className="h-4 w-4" /> },
      { key: PERMISSION_ACTIONS.CREATE, label: ACTION_LABELS[PERMISSION_ACTIONS.CREATE], icon: <Plus className="h-4 w-4" /> },
      { key: PERMISSION_ACTIONS.EDIT, label: ACTION_LABELS[PERMISSION_ACTIONS.EDIT], icon: <Edit className="h-4 w-4" /> }
    ];
    
          // Organizar módulos conforme hierarquia do sidebar
      const organizedModules = MAIN_MODULES_ORDER.map(mainModuleName => {
        const mainModule = modules.find(m => m.name === mainModuleName);
        if (!mainModule) return null;
        
        const submoduleNames = getSubmodules(mainModuleName);
        const submodules = submoduleNames
          .map(subName => modules.find(m => m.name === subName))
          .filter(Boolean);
        
        return {
          main: mainModule,
          subs: submodules
        };
      }).filter(Boolean);
    
    return (
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold text-foreground w-[300px]">
                Módulo / Página
              </TableHead>
              {actions.map(action => {
                const actionPermissions = permissions.filter(p => p.action === action.key);
                const checkedPermissions = actionPermissions.filter(p => hasPermission(p.id));
                const allChecked = checkedPermissions.length === actionPermissions.length && actionPermissions.length > 0;
                const someChecked = checkedPermissions.length > 0;
                
                return (
                  <TableHead key={action.key} className="text-center font-semibold text-foreground w-[120px]">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        {action.icon}
                        {action.label}
                      </div>
                      <button
                        onClick={() => toggleAllPermissionsForAction(action.key)}
                        disabled={!selectedRole || isSystemRole}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          allChecked 
                            ? 'bg-primary text-primary-foreground hover:bg-primary/80' 
                            : someChecked
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={allChecked ? 'Desmarcar todas' : 'Marcar todas'}
                      >
                        {allChecked ? 'Todas' : someChecked ? 'Algumas' : 'Nenhuma'}
                      </button>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
                      <TableBody>
             {organizedModules.length === 0 ? (
               <TableRow>
                 <TableCell colSpan={4} className="text-center py-12">
                   <div className="text-muted-foreground">
                     <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                     <p className="font-medium mb-2">Nenhum módulo encontrado</p>
                     <div className="text-xs space-y-1">
                       <p>Módulos no estado: {modules.length}</p>
                       <p>Módulos organizados: {organizedModules.length}</p>
                       <p className="mt-2 text-orange-600">Execute o script SQL para atualizar a estrutura do banco</p>
                     </div>
                   </div>
              </TableCell>
            </TableRow>
             ) : (
               organizedModules.map((moduleGroup, index) => {
              if (!moduleGroup) return null;
              
              const { main: mainModule, subs: subModules } = moduleGroup;
              const modulePermissions = getModulePermissions(mainModule.id);
              
              return (
                <React.Fragment key={mainModule.id}>
                  {/* Módulo principal */}
                  <TableRow className="bg-primary/5 border-b-2">
                    <TableCell className="font-semibold text-primary">
                      <div className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {mainModule.name}
                        {mainModule.name === MODULE_NAMES.SETTINGS && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (+ páginas não listadas no sidebar)
                          </span>
                        )}
                  </div>
                </TableCell>
                    {actions.map(action => {
                      const permission = modulePermissions.find(p => p.action === action.key);
                      const isChecked = permission ? hasPermission(permission.id) : false;
                      
                      return (
                        <TableCell key={action.key} className="text-center">
                          {permission ? (
                            <div className="flex justify-center">
                              <Switch
                                checked={isChecked}
                    onCheckedChange={() => togglePermission(permission.id)}
                                disabled={!selectedRole || isSystemRole}

                              />
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          )}
                </TableCell>
                      );
                    })}
              </TableRow>
                  
                  {/* Submódulos */}
                  {subModules.map(subModule => {
                    const subModulePermissions = getModulePermissions(subModule.id);
                    
                    return (
                      <TableRow key={subModule.id} className="hover:bg-accent/30">
                        <TableCell className="pl-8 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-px bg-border"></div>
                            {subModule.name}
                          </div>
                        </TableCell>
                        {actions.map(action => {
                          const permission = subModulePermissions.find(p => p.action === action.key);
                          const isChecked = permission ? hasPermission(permission.id) : false;
                          
                          return (
                            <TableCell key={action.key} className="text-center">
                              {permission ? (
                                <div className="flex justify-center">
                                  <Switch
                                    checked={isChecked}
                                    onCheckedChange={() => togglePermission(permission.id)}
                                    disabled={!selectedRole || isSystemRole}
    
                                  />
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">N/A</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
          </React.Fragment>
              );
                           })
             )}
            </TableBody>
        </Table>
      </div>
    );
  };
  
  // Se não for admin, mostrar acesso negado
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="container mx-auto py-12">
          <div className="max-w-md mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
                <CardTitle className="text-xl text-red-600 dark:text-red-400">Acesso Restrito</CardTitle>
                <CardDescription className="text-sm">
                  Esta página é exclusiva para administradores do sistema
            </CardDescription>
          </CardHeader>
              <CardContent className="text-center pb-2">
                <p className="text-sm text-muted-foreground mb-6">
                  Você não possui as permissões necessárias para acessar o controle de acesso.
                </p>
          </CardContent>
          <CardFooter>
                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full"
                  variant="outline"
                >
                  Voltar ao Dashboard
                </Button>
          </CardFooter>
        </Card>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                Controle de Acesso
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Gerencie funções, permissões e acesso de usuários
          </p>
        </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setIsRoleDialogOpen(true)}
                className="shadow-sm"
              >
            <Shield className="h-4 w-4 mr-2" />
            Nova Função
          </Button>
              <Button 
                onClick={() => setIsUserDialogOpen(true)}
                className="shadow-sm"
              >
            <UserPlus className="h-4 w-4 mr-2" />
            Atribuir Função
          </Button>
            </div>
        </div>
      </div>

        {/* Main Content */}
        <div className="space-y-6">
          <Tabs defaultValue="roles" className="space-y-6">
            <div className="flex items-center justify-center">
              <TabsList className="grid grid-cols-2 w-full max-w-md">
                <TabsTrigger value="roles" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Funções
              </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                Usuários
              </TabsTrigger>
            </TabsList>
            </div>

            <TabsContent value="roles" className="space-y-6">
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
                    <p className="text-sm text-muted-foreground">Carregando funções...</p>
                  </div>
                </div>
              ) : (
                <div className="grid lg:grid-cols-[320px_1fr] gap-6">
                  {/* Lista de funções */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-primary" />
                        Funções do Sistema
                      </CardTitle>
                      <CardDescription>
                        Selecione uma função para gerenciar permissões
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-[500px] overflow-y-auto">
                        {roles.map(role => (
                          <div 
                            key={role.id}
                            onClick={() => setSelectedRole(role.id)}
                            className={`px-4 py-3 cursor-pointer border-b border-border/50 transition-all hover:bg-accent/50 ${
                              selectedRole === role.id 
                                ? 'bg-primary/10 border-l-4 border-l-primary' 
                                : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{role.name}</p>
                                {role.description && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {role.description}
                                  </p>
                                )}
                              </div>
                              {role.is_system && (
                                <Badge variant="secondary" className="text-xs">
                                  Sistema
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Permissões da função selecionada */}
                  {selectedRole && (
                    <Card className="border-0 shadow-lg">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Settings className="h-5 w-5 text-primary" />
                          Permissões: {roles.find(r => r.id === selectedRole)?.name}
                        </CardTitle>
                        <CardDescription>
                              Configure o que esta função pode acessar no sistema
                        </CardDescription>
                          </div>
                          {roles.find(r => r.id === selectedRole)?.is_system && (
                            <Badge variant="outline" className="text-xs">
                              Função do Sistema
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Resumo de permissões */}
                        {selectedRole && (
                          <div className="mb-6 p-4 bg-accent/20 rounded-lg border">
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <div className="text-2xl font-bold text-primary">
                                  {permissions.filter(p => p.action === PERMISSION_ACTIONS.VIEW && hasPermission(p.id)).length}
                                </div>
                                <div className="text-xs text-muted-foreground">Pode Visualizar</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold text-green-600">
                                  {permissions.filter(p => p.action === PERMISSION_ACTIONS.CREATE && hasPermission(p.id)).length}
                                </div>
                                <div className="text-xs text-muted-foreground">Pode Criar</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold text-orange-600">
                                  {permissions.filter(p => p.action === PERMISSION_ACTIONS.EDIT && hasPermission(p.id)).length}
                                </div>
                                <div className="text-xs text-muted-foreground">Pode Editar</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="space-y-4">
                          <div className="text-sm text-muted-foreground bg-accent/20 p-3 rounded-lg">
                            <p className="font-medium mb-1">📝 Organização dos Módulos:</p>
                            <ul className="text-xs space-y-1">
                              <li>• <strong>Módulos principais:</strong> Conforme estrutura do sidebar</li>
                              <li>• <strong>Configurações:</strong> Inclui todas as páginas não listadas no sidebar</li>
                              <li>• <strong>Ações em lote:</strong> Use os botões no cabeçalho das colunas</li>
                            </ul>
                          </div>
                          
                          <div className="max-h-[600px] overflow-y-auto">
                            {renderPermissionsMatrix()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="users" className="space-y-6">
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
                    <p className="text-sm text-muted-foreground">Carregando usuários...</p>
                  </div>
                </div>
              ) : (
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="h-5 w-5 text-primary" />
                          Usuários do Sistema
                        </CardTitle>
                        <CardDescription>
                          Gerencie funções e permissões dos usuários
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {users.length} usuário{users.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {users.length === 0 ? (
                        <div className="text-center py-12">
                          <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                          <p className="text-muted-foreground">Nenhum usuário encontrado</p>
                        </div>
                      ) : (
                        users.map(user => (
                          <div 
                            key={user.id} 
                            className="flex items-center justify-between p-4 border border-border/50 rounded-lg hover:bg-accent/30 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                  <Users className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{user.email}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Criado em {new Date(user.created_at).toLocaleDateString('pt-BR')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 ml-11">
                                {user.user_roles.length === 0 ? (
                                  <Badge variant="outline" className="text-xs">
                                    Sem funções
                                  </Badge>
                                ) : (
                                  user.user_roles.map(ur => (
                                  <div 
                                    key={ur.role.id} 
                                      className="flex items-center gap-1 bg-primary/10 border border-primary/20 rounded-md px-2 py-1"
                                  >
                                      <span className="text-xs font-medium">{ur.role.name}</span>
                                    <button 
                                      onClick={() => removeRoleFromUser(user.id, ur.role.id)}
                                        className="ml-1 rounded-full hover:bg-destructive/20 p-0.5 transition-colors"
                                        title="Remover função"
                                    >
                                        <UserX className="h-3 w-3 text-destructive" />
                                    </button>
                                  </div>
                                  ))
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"

                                onClick={() => {
                                  setSelectedUser(user);
                                  setIsUserDialogOpen(true);
                                }}
                                className="shadow-sm"
                              >
                                <UserCheck className="h-4 w-4 mr-1" />
                                Atribuir
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>


          </Tabs>
        </div>
      </div>

      {/* Dialog para atribuir função a usuário */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Atribuir Função
            </DialogTitle>
            <DialogDescription>
              {selectedUser 
                ? `Adicionar nova função para ${selectedUser.email}` 
                : 'Selecione um usuário e uma função para atribuir'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {!selectedUser && (
              <div className="space-y-3">
                <Label htmlFor="user" className="text-sm font-medium">Usuário</Label>
                <Select onValueChange={(value) => {
                  const user = users.find(u => u.id === value) || null;
                  setSelectedUser(user);
                }}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Escolha um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                        {user.email}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-3">
              <Label htmlFor="role" className="text-sm font-medium">Função</Label>
              <Select onValueChange={setUserRoleId}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Escolha uma função" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <span>{role.name}</span>
                        </div>
                        {role.is_system && (
                          <Badge variant="secondary" className="text-xs ml-2">
                            Sistema
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
              setIsUserDialogOpen(false);
              setSelectedUser(null);
              setUserRoleId(null);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              onClick={assignRoleToUser}
              disabled={!selectedUser || !userRoleId}
              className="flex-1"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Atribuir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog para criar nova função */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Nova Função
            </DialogTitle>
            <DialogDescription>
              Crie uma nova função para organizar permissões de usuários
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <Label htmlFor="role-name" className="text-sm font-medium">Nome da Função</Label>
              <Input
                id="role-name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Ex: Consultor Pedagógico, Editor, etc."
                className="h-10"
              />
            </div>
            
            <div className="space-y-3">
              <Label htmlFor="role-description" className="text-sm font-medium">Descrição (opcional)</Label>
              <Input
                id="role-description"
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
                placeholder="Descreva a finalidade desta função"
                className="h-10"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
              setIsRoleDialogOpen(false);
              setNewRoleName('');
              setNewRoleDescription('');
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              onClick={createRole}
              disabled={!newRoleName.trim()}
              className="flex-1"
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessControl; 